/** Adding a step in the Builder (ADR-0071 phase 3, board 14): the plain chooser, three questions, done. */
export default {
  en: {
    button: "Add a step",
    plus: "Add a step between ‘{{from}}’ and ‘{{to}}’",
    title: "Add a step",
    where: {
      edge: "Between ‘{{from}}’ and ‘{{to}}’.",
      after: "After ‘{{from}}’.",
      none: "Not connected yet: draw an arrow to it, or pick a step first.",
      answer: "For the answer ‘{{answer}}’.",
    },
    stage: { choose: "What should this step do?", setup: "Set up the new step", done: "Added. Here's what changed" },
    group: { think: "Think or write", info: "Get or change information", person: "Involve a person", flow: "Control the flow" },
    eg: "e.g. {{text}}",
    roleWarn: "Needs the {{roles}} role, which you don't have.",
    kind: {
      ai: { name: "Ask the AI", what: "Read, decide, summarise or write something.", eg: "pick the 3 best courts" },
      code: { name: "Run code", what: "Your own Python: calculate, check, reshape data.", eg: "convert prices to AED" },
      calc: { name: "Calculate or set values", what: "Work out totals, VAT, tolerances, dates and Hijri dates, or set and copy values — no AI, no code.", eg: "total with 15% VAT, due in 5 working days" },
      system: { name: "Use a system", what: "Look up or change something in a connected system.", eg: "read an invoice from the ERP" },
      web: { name: "Open a website", what: "Read pages with the web browser (allowed sites only).", eg: "read a club's booking page" },
      knowledge: { name: "Search knowledge", what: "Find passages in a knowledge collection.", eg: "search the booking policy" },
      table: { name: "Work with a table", what: "Read a spreadsheet or CSV as rows, match two lists (with tolerances), or group and total.", eg: "match the bank statement with the ledger within 0.01" },
      agent: { name: "Use another agent", what: "Hand part of the job to an agent you already have.", eg: "ask the translator agent" },
      approve: { name: "Ask a person to approve", what: "Wait until someone approves or rejects.", eg: "approve a booking over 500 AED" },
      ask: { name: "Ask a person for information", what: "Ask a question and wait for the answer.", eg: "which club do you prefer?" },
      handover: { name: "Hand over to a person", what: "A person on the team takes over the conversation; the agent stops answering.", eg: "the customer asks for a refund" },
      choose: { name: "Choose a path", what: "Go different ways by a rule, or let the AI choose.", eg: "if no courts → say sorry" },
      parallel: { name: "Do several things at once", what: "Run steps side by side, then wait for all.", eg: "read 8 clubs at the same time" },
      foreach: { name: "For each item in a list", what: "Do the same steps for every item: each overdue invoice, each unmatched line.", eg: "for each overdue invoice, draft and send a reminder" },
      await: { name: "Wait for something", what: "Pause until another system tells us, a time comes or a person answers.", eg: "wait for the payment, at most 3 days" },
      end: { name: "End here", what: "Finish the run on this path.", eg: "stop after small talk" },
    },
    q: {
      name: "Name it",
      nameEn: "Name (English)",
      nameAr: "Name (Arabic)",
      aiDo: "What should the AI do?",
      aiGives: "What should it give back?",
      givesText: "Text",
      givesFields: "Fields later steps can use",
      fieldNames: "The fields (names in code, e.g. club, time, why)",
      aiSystems: "May it use a system?",
      noSystems: "No",
      codeWhich: "Which code?",
      codeFile: "A function in the agent's file",
      codeNew: "A short new program (edit it after adding)",
      codeFn: "Function",
      codeReads: "What does it read?",
      codeGives: "What fields does it give back? (names in code; or fill them from a test run later)",
      webPages: "Which pages?",
      webFixed: "A fixed address",
      webFrom: "Or the address from an earlier step",
      webMany: "Several pages",
      webRead: "What should it read?",
      webText: "The page text",
      webScript: "Fields, with a page script",
      sysWhich: "Which system and action?",
      sysSends: "What it sends is set in the step after adding (a form from the action).",
      sysNeeds: "What it needs (type a value, or type {{ to use one from earlier steps)",
      sysSendsMore: "Anything else it can send is set in the step after adding.",
      makes: "Name what it gives back",
      makesEn: "Name (English)",
      makesAr: "Name (Arabic)",
      kbWhich: "Which knowledge?",
      kbQuery: "Search for what?",
      kbQueryHelp: "Empty means: what the person asked.",
      agentWhich: "Which agent and version?",
      approveWhat: "What should they approve?",
      approveWho: "Who approves, and how long do they have?",
      approveReject: "If they reject?",
      rejectFinish: "Finish",
      askQuestion: "What do you ask?",
      askKind: "What kind of answer?",
      askChoices: "The choices",
      chooseHow: "How should the path be chosen?",
      byRule: "By a rule",
      byAi: "The AI decides",
      chooseRule: "Set the paths in the step after adding; until then everything goes on to ‘{{to}}’.",
      chooseRuleNone: "Set the paths in the step after adding.",
      chooseQuestion: "What should the AI decide?",
      chooseOptions: "The possible answers",
      chooseOptionsHelp: "One row per answer, in Arabic and English. Each answer gets its own arrow; you can then add a step on it.",
      optionAr: "Answer {{n}} (Arabic)",
      optionEn: "Answer {{n}} (English)",
      optionRemove: "Remove answer {{n}}",
      optionAdd: "Add an answer",
      chooseUnsure: "When the AI isn't sure",
      chooseSure: "How sure it must be",
      chooseUnsureDo: "What happens when it is less sure",
      unsureAsk: "Ask a person to choose",
      unsureGo: "Go to ‘{{step}}’",
      chooseSureHelp: "Below {{n}}% sure, the AI doesn't choose on its own.",
      forAnswer: "Which answer is this step for?",
      forAnswerPick: "Pick an answer",
      answerGoes: "now goes to ‘{{step}}’",
      answerNoPath: "no path yet",
      parallelSteps: "Which steps run at the same time? (2 or more)",
      parallelNewHelp: "Name each new step and say what it does; they are made with this step and set up after.",
      branchAr: "Step {{n}} (Arabic)",
      branchEn: "Step {{n}} (English)",
      branchKind: "What step {{n}} does",
      branchRemove: "Remove step {{n}}",
      branchAdd: "Add another step",
      parallelExisting: "Steps you already have that also run at the same time (optional)",
      parallelCombine: "How to combine the results?",
      endNone: "No questions: the run ends here on this path.",
      handoverWhy: "Why should a person take over? (shown to the team)",
      handoverWhyEn: "Why (English)",
      handoverWhyAr: "Why (Arabic)",
      handoverWhere: "In website chat, chat apps and e-mail the conversation goes to the team (Needs you); the person is told someone will reply. Usually the last step on its path.",
    },
    defaults: {
      label: "Set for you (change later in Edit): {{text}}",
      ai: "the agent's model with its fallback · stop if it fails · sees the chat, earlier results and attachments",
      code: "30 s · 256 MB · no internet · keeps the result",
      calc: "stops the run and says why if a value can't be worked out",
      table: "up to 5,000 rows a table · read only, nothing changes · an attached file is checked like any attachment",
      foreach: "4 items at a time · at most 100 items · a failed item is skipped and noted · the run's budget covers all items",
      web: "one page unless you choose several · the tool's pause and time limit",
      system: "the agent's approval rule if the action changes data",
      knowledge: "searches what the person asked · up to 8 passages",
      agent: "the version you pick",
      approve: "role approver · 4 hours · finish if rejected",
      await: "gives up after 30 days and goes on as usual · counts calendar time",
      ask: "a text answer",
      choose: "otherwise → the step it came before",
      chooseRule: "no rules yet, so everything goes on to ‘{{to}}’ until you add them",
      chooseRuleNone: "no rules yet; set where it goes in the step after adding",
      chooseAi: "one arrow per answer to the step after it · a person chooses when the AI is unsure",
      parallel: "the “wait for all” step is created and wired",
      end: "nothing",
      handover: "the team’s queue and response time from Channels settings",
    },
    back: "Back",
    add: "Add step",
    close: "Close",
    show: "Show the step",
    done: {
      between: "‘{{name}}’ runs after ‘{{from}}’ and before ‘{{to}}’.",
      after: "‘{{name}}’ runs after ‘{{from}}’.",
      alone: "‘{{name}}’ is added; draw an arrow to it from the step before.",
      makes: "It writes {{value}} to the run's notebook.",
      wired: "The arrows were re-wired; nothing else moved.",
      checksOk: "Checks: all clear.",
      tierRaised: "The agent's risk level went from {{from}} to {{tier}}, because this step needs it.",
      roleNeeded: "This step needs the {{roles}} role, which you don't have. You can keep it, but only someone with that role can try or run the agent.",
      checks_one: "Checks: 1 thing to look at in the new step:",
      checks_other: "Checks: {{count}} things to look at in the new step:",
      suggest: "‘{{step}}’ doesn't read {{value}} yet, so it won't use it.",
      suggestFix: "Let ‘{{step}}’ read it",
      suggested: "‘{{step}}’ now reads {{value}}.",
    },
  },
  ar: {
    button: "إضافة خطوة",
    plus: "إضافة خطوة بين ‹{{from}}› و‹{{to}}›",
    title: "إضافة خطوة",
    where: {
      edge: "بين ‹{{from}}› و‹{{to}}›.",
      after: "بعد ‹{{from}}›.",
      none: "غير موصولة بعد: ارسم سهمًا إليها، أو اختر خطوة أولًا.",
      answer: "للإجابة ‹{{answer}}›.",
    },
    stage: { choose: "ماذا تفعل هذه الخطوة؟", setup: "إعداد الخطوة الجديدة", done: "أُضيفت. هذا ما تغيّر" },
    group: { think: "التفكير أو الكتابة", info: "الحصول على معلومات أو تغييرها", person: "إشراك شخص", flow: "التحكم في المسار" },
    eg: "مثل: {{text}}",
    roleWarn: "يحتاج إلى دور {{roles}}، وهو ليس لديك.",
    kind: {
      ai: { name: "اسأل الذكاء الاصطناعي", what: "اقرأ أو قرّر أو لخّص أو اكتب شيئًا.", eg: "اختيار أفضل 3 ملاعب" },
      code: { name: "تشغيل شيفرة", what: "بايثون الخاص بك: احسب أو افحص أو أعد تشكيل البيانات.", eg: "تحويل الأسعار إلى الدرهم" },
      calc: { name: "حساب القيم أو تعيينها", what: "احسب المجاميع وضريبة القيمة المضافة وهوامش السماح والتواريخ والتواريخ الهجرية، أو عيّن القيم وانسخها — دون ذكاء اصطناعي ولا شيفرة.", eg: "المجموع مع ضريبة 15٪، والاستحقاق بعد 5 أيام عمل" },
      system: { name: "استخدام نظام", what: "ابحث عن شيء في نظام متصل أو غيّره.", eg: "قراءة فاتورة من نظام ERP" },
      web: { name: "فتح موقع", what: "اقرأ الصفحات بمتصفح الويب (المواقع المسموح بها فقط).", eg: "قراءة صفحة الحجز في نادٍ" },
      knowledge: { name: "البحث في المعرفة", what: "اعثر على مقاطع في مجموعة معرفة.", eg: "البحث في سياسة الحجز" },
      agent: { name: "استخدام وكيل آخر", what: "سلّم جزءًا من العمل إلى وكيل لديك.", eg: "اسأل وكيل الترجمة" },
      approve: { name: "اطلب من شخص الاعتماد", what: "انتظر حتى يوافق شخص أو يرفض.", eg: "اعتماد حجز يزيد على 500 درهم" },
      ask: { name: "اطلب من شخص معلومة", what: "اطرح سؤالًا وانتظر الإجابة.", eg: "أي نادٍ تفضّل؟" },
      handover: { name: "التحويل إلى شخص", what: "يتولى أحد أعضاء الفريق المحادثة ويتوقف الوكيل عن الرد.", eg: "يطلب العميل استرداد المبلغ" },
      choose: { name: "اختيار مسار", what: "اسلك طرقًا مختلفة بقاعدة، أو دع الذكاء الاصطناعي يختار.", eg: "إذا لا ملاعب ← اعتذر" },
      parallel: { name: "عدة أمور في وقت واحد", what: "شغّل الخطوات جنبًا إلى جنب، ثم انتظرها كلها.", eg: "قراءة 8 نوادٍ في الوقت نفسه" },
      table: { name: "العمل على جدول", what: "اقرأ جدول بيانات أو CSV صفوفاً، أو طابق قائمتين (بهامش سماح)، أو جمّع وأجمل.", eg: "مطابقة كشف البنك مع دفتر الأستاذ بفرق 0.01" },
      foreach: { name: "لكل عنصر في قائمة", what: "نفّذ الخطوات نفسها لكل عنصر: كل فاتورة متأخرة، كل سطر غير مطابق.", eg: "لكل فاتورة متأخرة، صغ تذكيراً وأرسله" },
      await: { name: "انتظار شيء ما", what: "توقّف حتى يخبرنا نظام آخر أو يحين موعد أو يجيب شخص.", eg: "انتظار الدفع، 3 أيام على الأكثر" },
      end: { name: "الإنهاء هنا", what: "أنهِ التشغيل على هذا المسار.", eg: "التوقف بعد الدردشة القصيرة" },
    },
    q: {
      name: "سمّها",
      nameEn: "الاسم (بالإنجليزية)",
      nameAr: "الاسم (بالعربية)",
      aiDo: "ماذا يجب أن يفعل الذكاء الاصطناعي؟",
      aiGives: "ماذا يجب أن يُرجع؟",
      givesText: "نصًا",
      givesFields: "حقولًا تستخدمها الخطوات اللاحقة",
      fieldNames: "الحقول (أسماؤها في الشيفرة، مثل club وtime وwhy)",
      aiSystems: "هل يمكنه استخدام نظام؟",
      noSystems: "لا",
      codeWhich: "أي شيفرة؟",
      codeFile: "دالة في ملف الوكيل",
      codeNew: "برنامج قصير جديد (عدّله بعد الإضافة)",
      codeFn: "الدالة",
      codeReads: "ماذا تقرأ؟",
      codeGives: "ما الحقول التي تُرجعها؟ (أسماؤها في الشيفرة، أو املأها لاحقًا من تشغيل تجريبي)",
      webPages: "أي الصفحات؟",
      webFixed: "عنوان ثابت",
      webFrom: "أو العنوان من خطوة سابقة",
      webMany: "عدة صفحات",
      webRead: "ماذا يجب أن تقرأ؟",
      webText: "نص الصفحة",
      webScript: "حقولًا، بنص برمجي للصفحة",
      sysWhich: "أي نظام وأي إجراء؟",
      sysSends: "يُضبط ما يُرسَل في الخطوة بعد الإضافة (نموذج من الإجراء).",
      sysNeeds: "ما يحتاج إليه (اكتب قيمة، أو اكتب {{ لاستخدام قيمة من خطوة سابقة)",
      sysSendsMore: "أي شيء آخر يمكن إرساله يُضبط في الخطوة بعد الإضافة.",
      makes: "سمِّ ما تُرجعه",
      makesEn: "الاسم (بالإنجليزية)",
      makesAr: "الاسم (بالعربية)",
      kbWhich: "أي معرفة؟",
      kbQuery: "عمّ تبحث؟",
      kbQueryHelp: "الفراغ يعني: ما سأله الشخص.",
      agentWhich: "أي وكيل وأي إصدار؟",
      approveWhat: "ماذا يجب أن يعتمدوا؟",
      approveWho: "من يعتمد، وكم لديهم من الوقت؟",
      approveReject: "إذا رفضوا؟",
      rejectFinish: "الإنهاء",
      askQuestion: "ماذا تسأل؟",
      askKind: "ما نوع الإجابة؟",
      askChoices: "الخيارات",
      chooseHow: "كيف يُختار المسار؟",
      byRule: "بقاعدة",
      byAi: "يقرر الذكاء الاصطناعي",
      chooseRule: "اضبط المسارات في الخطوة بعد الإضافة؛ وحتى ذلك يذهب كل شيء إلى ‹{{to}}›.",
      chooseRuleNone: "اضبط المسارات في الخطوة بعد الإضافة.",
      chooseQuestion: "ماذا يجب أن يقرر الذكاء الاصطناعي؟",
      chooseOptions: "الإجابات الممكنة",
      chooseOptionsHelp: "صف لكل إجابة، بالعربية والإنجليزية. لكل إجابة سهم خاص بها، ويمكنك بعدها إضافة خطوة عليه.",
      optionAr: "الإجابة {{n}} (بالعربية)",
      optionEn: "الإجابة {{n}} (بالإنجليزية)",
      optionRemove: "إزالة الإجابة {{n}}",
      optionAdd: "إضافة إجابة",
      chooseUnsure: "عندما لا يكون الذكاء الاصطناعي متأكدًا",
      chooseSure: "مدى التأكد المطلوب",
      chooseUnsureDo: "ماذا يحدث عندما يكون أقل تأكدًا",
      unsureAsk: "اطلب من شخص أن يختار",
      unsureGo: "الانتقال إلى ‹{{step}}›",
      chooseSureHelp: "إذا قلّ التأكد عن {{n}}٪ لا يختار الذكاء الاصطناعي وحده.",
      forAnswer: "لأي إجابة هذه الخطوة؟",
      forAnswerPick: "اختر إجابة",
      answerGoes: "تذهب الآن إلى ‹{{step}}›",
      answerNoPath: "بلا مسار بعد",
      parallelSteps: "أي الخطوات تعمل في الوقت نفسه؟ (اثنتان أو أكثر)",
      parallelNewHelp: "سمِّ كل خطوة جديدة وحدّد ما تفعله؛ تُنشأ مع هذه الخطوة وتُضبط بعد ذلك.",
      branchAr: "الخطوة {{n}} (بالعربية)",
      branchEn: "الخطوة {{n}} (بالإنجليزية)",
      branchKind: "ما تفعله الخطوة {{n}}",
      branchRemove: "إزالة الخطوة {{n}}",
      branchAdd: "إضافة خطوة أخرى",
      parallelExisting: "خطوات لديك تعمل أيضًا في الوقت نفسه (اختياري)",
      parallelCombine: "كيف تُجمع النتائج؟",
      endNone: "لا أسئلة: ينتهي التشغيل هنا على هذا المسار.",
      handoverWhy: "لماذا يجب أن يتولى شخص المحادثة؟ (يظهر للفريق)",
      handoverWhyEn: "السبب (بالإنجليزية)",
      handoverWhyAr: "السبب (بالعربية)",
      handoverWhere: "في دردشة الموقع وتطبيقات الدردشة والبريد الإلكتروني تنتقل المحادثة إلى الفريق (يحتاج إليك)، ويُبلَّغ الشخص بأن أحدًا سيرد عليه. عادةً تكون آخر خطوة في مسارها.",
    },
    defaults: {
      label: "ضُبط لك (غيّره لاحقًا في التعديل): {{text}}",
      ai: "نموذج الوكيل مع بديله · الإيقاف إذا فشل · يرى المحادثة والنتائج السابقة والمرفقات",
      code: "30 ثانية · 256 ميغابايت · بلا إنترنت · يحتفظ بالنتيجة",
      calc: "يوقف التشغيل ويبيّن السبب إذا تعذّر حساب قيمة",
      table: "حتى 5,000 صف للجدول · قراءة فقط ولا يتغير شيء · يُفحص الملف المرفق كأي مرفق",
      foreach: "4 عناصر في الوقت نفسه · 100 عنصر على الأكثر · يُتخطّى العنصر الفاشل ويُسجَّل · تغطي ميزانية التشغيل كل العناصر",
      web: "صفحة واحدة ما لم تختر عدة صفحات · توقف الأداة ومهلتها",
      system: "قاعدة الاعتماد في الوكيل إذا كان الإجراء يغيّر البيانات",
      knowledge: "يبحث عمّا سأله الشخص · حتى 8 مقاطع",
      agent: "الإصدار الذي تختاره",
      approve: "دور المعتمِد · 4 ساعات · الإنهاء إذا رُفض",
      await: "يتوقف عن الانتظار بعد 30 يوماً ويتابع كالمعتاد · يحتسب الوقت الفعلي",
      ask: "إجابة نصية",
      choose: "وإلا ← الخطوة التي كانت بعدها",
      chooseRule: "لا قواعد بعد، لذا يذهب كل شيء إلى ‹{{to}}› حتى تضيفها",
      chooseRuleNone: "لا قواعد بعد؛ حدّد وجهتها في الخطوة بعد الإضافة",
      chooseAi: "سهم لكل إجابة إلى الخطوة التالية · يختار شخص عندما لا يكون الذكاء الاصطناعي متأكدًا",
      parallel: "تُنشأ خطوة «انتظار الكل» وتوصل",
      end: "لا شيء",
      handover: "قائمة الفريق ومدة الرد من إعدادات القنوات",
    },
    back: "رجوع",
    add: "إضافة الخطوة",
    close: "إغلاق",
    show: "عرض الخطوة",
    done: {
      between: "تعمل ‹{{name}}› بعد ‹{{from}}› وقبل ‹{{to}}›.",
      after: "تعمل ‹{{name}}› بعد ‹{{from}}›.",
      alone: "أُضيفت ‹{{name}}›؛ ارسم سهمًا إليها من الخطوة السابقة.",
      makes: "تكتب {{value}} في دفتر التشغيل.",
      wired: "أُعيد توصيل الأسهم؛ ولم يتحرك شيء آخر.",
      checksOk: "الفحوص: كل شيء سليم.",
      tierRaised: "ارتفع مستوى خطورة الوكيل من {{from}} إلى {{tier}}، لأن هذه الخطوة تحتاج إليه.",
      roleNeeded: "تحتاج هذه الخطوة إلى دور {{roles}}، وهو ليس لديك. يمكنك إبقاؤها، لكن لا يجرّب الوكيل أو يشغّله إلا من لديه هذا الدور.",
      checks_zero: "الفحوص: كل شيء سليم.",
      checks_one: "الفحوص: أمر واحد يستحق النظر في الخطوة الجديدة:",
      checks_two: "الفحوص: أمران يستحقان النظر في الخطوة الجديدة:",
      checks_few: "الفحوص: {{count}} أمور تستحق النظر في الخطوة الجديدة:",
      checks_many: "الفحوص: {{count}} أمرًا يستحق النظر في الخطوة الجديدة:",
      checks_other: "الفحوص: {{count}} أمر يستحق النظر في الخطوة الجديدة:",
      suggest: "‹{{step}}› لا تقرأ {{value}} بعد، فلن تستخدمها.",
      suggestFix: "اجعل ‹{{step}}› تقرؤها",
      suggested: "صارت ‹{{step}}› تقرأ {{value}}.",
    },
  },
};
